"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { AlertCircle } from "@/components/icons";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { useAuth } from "@/lib/auth/AuthProvider";
import { ERRORES_GOOGLE, type ErrorGoogle } from "@/lib/auth/errores";
import { GoogleSignInButton } from "./GoogleSignInButton";
import { ViewHeader } from "./ViewHeader";

const MENSAJE_ERROR_GOOGLE = {
  cancelado: "login.googleErrorCancelado",
  "sin-alta": "login.googleErrorSinAlta",
  dominio: "login.googleErrorDominio",
  inactivo: "login.googleErrorInactivo",
  fallo: "login.googleErrorFallo",
} as const satisfies Record<ErrorGoogle, string>;

// El aviso aparece con un fundido y 4 px de subida; la altura cambia sin animarse (no se animan
// propiedades de diseño). Con "reducir movimiento" Motion lo omite.
const aparecer = {
  initial: { opacity: 0, y: -4 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, transition: { duration: 0.12 } },
  transition: { duration: 0.2, ease: [0.32, 0.72, 0, 1] },
} as const;

/**
 * Error con el que el servidor devuelve al login tras pasar por Google (`/?error=sin-alta`).
 * Solo se aceptan los códigos conocidos: el texto que se muestra nunca sale de la URL.
 */
function leerErrorGoogle(): ErrorGoogle | null {
  const codigo = new URLSearchParams(window.location.search).get("error");
  return ERRORES_GOOGLE.find((e) => e === codigo) ?? null;
}

// Acceso con la cuenta de Google de la empresa (docs/plan-backend-google-cloud.md, sección 3). RUT y contraseña
// queda como plan B para quien no tenga cuenta de Workspace (fase 3 de docs/plan-login.md): hasta entonces no hay
// credenciales que comparar, así que no se ofrece.
export function LoginView() {
  const { t } = useI18n();
  const { loginWithGoogle } = useAuth();

  const [errorGoogle, setErrorGoogle] = useState<ErrorGoogle | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);

  // La página se renderiza primero en el servidor, que no ve la URL del navegador: el error se lee al montar.
  useEffect(() => {
    const codigo = leerErrorGoogle();
    if (!codigo) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setErrorGoogle(codigo);
    // Quita ?error= de la dirección para que el mensaje no reaparezca al recargar.
    window.history.replaceState(null, "", window.location.pathname);
  }, []);

  // Al volver atrás desde Google el navegador restaura la página tal como estaba, con el botón ocupado.
  useEffect(() => {
    const alVolver = (e: PageTransitionEvent) => {
      if (e.persisted) setGoogleBusy(false);
    };
    window.addEventListener("pageshow", alVolver);
    return () => window.removeEventListener("pageshow", alVolver);
  }, []);

  function handleGoogle() {
    setGoogleBusy(true);
    loginWithGoogle();
  }

  return (
    <div className="flex w-full flex-col">
      <ViewHeader title={t("login.welcomeTitle")} subtitle={t("login.subtitle")} />

      <GoogleSignInButton label={t("login.googleSignIn")} onClick={handleGoogle} busy={googleBusy} />

      <AnimatePresence initial={false}>
        {errorGoogle && (
          <motion.div key="error" {...aparecer}>
            <div role="alert" className="mt-3 flex gap-3 rounded-control bg-destructive/10 px-4 py-3 text-left">
              <AlertCircle className="mt-px h-[18px] w-[18px] shrink-0 text-destructive" />
              <p className="text-[13px] leading-[1.4] text-destructive">
                <span className="font-semibold">{t("login.googleErrorTitle")}.</span> {t(MENSAJE_ERROR_GOOGLE[errorGoogle])}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="mt-6 text-center text-[13px] leading-[1.45] text-muted-foreground">{t("login.googleOnlyNote")}</p>
    </div>
  );
}
